import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Star, LogOut, Trash2, Check, X, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('gallery');
  const [token, setToken] = useState('');
  
  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  
  // Reviews state
  const [pendingReviews, setPendingReviews] = useState([]);
  
  // Contacts state
  const [contactSubmissions, setContactSubmissions] = useState([]);

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) {
      navigate('/admin/login');
      return;
    }
    setToken(storedToken);
    fetchData(storedToken);
  }, [navigate]);

  const fetchData = async (authToken) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` }
      };

      const [galleryRes, reviewsRes, contactsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/gallery`, config),
        axios.get(`${BACKEND_URL}/api/admin/reviews/pending`, config),
        axios.get(`${BACKEND_URL}/api/admin/contacts`, config)
      ]);

      setGalleryImages(galleryRes.data);
      setPendingReviews(reviewsRes.data);
      setContactSubmissions(contactsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const addGalleryImage = async () => {
    if (!newImageUrl.trim()) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/gallery?url=${encodeURIComponent(newImageUrl)}&order=999`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Image added successfully!');
      setNewImageUrl('');
      fetchData(token);
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const deleteGalleryImage = async (imageId) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Image deleted successfully!');
      fetchData(token);
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const approveReview = async (reviewId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/reviews/${reviewId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Review approved!');
      fetchData(token);
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!confirm('Are you sure you want to delete this review?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Review deleted!');
      fetchData(token);
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <div className="bg-[#1a1a1b] border-b border-[#D4AF37]/20 px-4 py-4">
        <div className="container mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#D4AF37]">Admin Dashboard</h1>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#1a1a1b] border border-[#D4AF37]/20">
            <TabsTrigger value="gallery">Gallery ({galleryImages.length})</TabsTrigger>
            <TabsTrigger value="reviews">Reviews ({pendingReviews.length})</TabsTrigger>
            <TabsTrigger value="contacts">Contacts ({contactSubmissions.length})</TabsTrigger>
          </TabsList>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="mt-6">
            <Card className="bg-[#1a1a1b] border-[#D4AF37]/20 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Add New Image</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Paste image URL here"
                    className="bg-[#0a0a0b] border-[#D4AF37]/30 text-white"
                  />
                  <Button
                    onClick={addGalleryImage}
                    className="bg-[#D4AF37] text-black hover:bg-[#C4A037]"
                  >
                    Add Image
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Upload your image to a hosting service first, then paste the URL here
                </p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryImages.map((image) => (
                <Card key={image.id} className="bg-[#1a1a1b] border-[#D4AF37]/20 overflow-hidden">
                  <div className="aspect-square relative group">
                    <img
                      src={image.url}
                      alt="Gallery"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteGalleryImage(image.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <a href={image.url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="mt-6">
            <div className="space-y-4">
              {pendingReviews.length === 0 ? (
                <Card className="bg-[#1a1a1b] border-[#D4AF37]/20">
                  <CardContent className="p-8 text-center text-gray-400">
                    No pending reviews
                  </CardContent>
                </Card>
              ) : (
                pendingReviews.map((review) => (
                  <Card key={review.id} className="bg-[#1a1a1b] border-[#D4AF37]/20">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-bold text-white">{review.name}</p>
                          <p className="text-sm text-gray-400">{review.email}</p>
                          <div className="flex gap-1 mt-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating
                                    ? 'fill-[#D4AF37] text-[#D4AF37]'
                                    : 'text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveReview(review.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteReview(review.id)}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                      <p className="text-gray-300 italic">"{review.review_text}"</p>
                      {review.service_type && (
                        <p className="text-sm text-gray-500 mt-2">Service: {review.service_type}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="mt-6">
            <div className="space-y-4">
              {contactSubmissions.length === 0 ? (
                <Card className="bg-[#1a1a1b] border-[#D4AF37]/20">
                  <CardContent className="p-8 text-center text-gray-400">
                    No contact submissions yet
                  </CardContent>
                </Card>
              ) : (
                contactSubmissions.map((contact) => (
                  <Card key={contact.id} className="bg-[#1a1a1b] border-[#D4AF37]/20">
                    <CardContent className="p-6">
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-400">Name</p>
                          <p className="font-bold text-white">{contact.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Email</p>
                          <a
                            href={`mailto:${contact.email}`}
                            className="text-[#D4AF37] hover:underline"
                          >
                            {contact.email}
                          </a>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Phone</p>
                          <a
                            href={`tel:${contact.phone}`}
                            className="text-[#D4AF37] hover:underline"
                          >
                            {contact.phone}
                          </a>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Service</p>
                          <p className="text-white">{contact.service || 'Not specified'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-2">Message</p>
                        <p className="text-gray-300">{contact.message}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Submitted: {new Date(contact.submitted_at).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
